import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import type { GlobalSearchAction } from 'contexts/GlobalSearch/GlobalSearchState'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { DATE_SEARCH_OPTION_IDS, DEFAULT_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { cloneOccupancies, isValidOccupancy, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getDateSearchOptionId, getHotelDateSelectionFromSearchState, type ListingDrawerHotelDateSelection } from '../components/ListingDrawerSearchDates'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { SearchMenuStates } from 'components/Search/type'

interface Params {
  searchDispatch: React.Dispatch<GlobalSearchAction>
  committedHotelDateSelection: ListingDrawerHotelDateSelection
  defaultRooms: Array<App.Occupants>
  isCompactDatePicker: boolean
  commitHotelDateSelection: (selectionOverride?: ListingDrawerHotelDateSelection) => void
  commitRoomsToParent: () => void
  areRoomsValid: boolean
  draftRooms: Array<App.Occupants>
  initiallyOpenGuestsPanel?: boolean
}

function useListingDrawerHotelSearchDraft({
  searchDispatch,
  committedHotelDateSelection,
  defaultRooms,
  isCompactDatePicker,
  commitHotelDateSelection,
  commitRoomsToParent,
  areRoomsValid,
  draftRooms,
  initiallyOpenGuestsPanel = false,
}: Params) {
  const [isHotelDatePanelOpen, setIsHotelDatePanelOpen] = useState(false)
  const [isHotelGuestsPanelOpen, setIsHotelGuestsPanelOpen] = useState(initiallyOpenGuestsPanel)
  const committedRoomsRef = useRef(defaultRooms)

  useEffect(() => {
    committedRoomsRef.current = defaultRooms
  }, [defaultRooms])

  const restoreCommittedDates = useCallback(() => {
    if (committedHotelDateSelection.type === 'specific') {
      searchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: moment(committedHotelDateSelection.checkIn) })
      searchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: moment(committedHotelDateSelection.checkOut) })
    } else {
      searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
      searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
    }

    searchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_DURATION })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_MONTH_RANGE })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS })

    if (committedHotelDateSelection.type === 'flexible') {
      searchDispatch({
        type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION,
        flexibleNights: committedHotelDateSelection.flexibleNights,
      })
      if (committedHotelDateSelection.flexibleMonths) {
        searchDispatch({
          type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE,
          flexibleMonths: committedHotelDateSelection.flexibleMonths,
        })
      }
      if (committedHotelDateSelection.userSelectedFlexibleMonths) {
        searchDispatch({
          type: GlobalSearchStateActions.SET_USER_SELECTED_FLEXIBLE_MONTHS,
          userSelectedFlexibleMonths: true,
        })
      }
    }

    searchDispatch({
      type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION,
      optionId: getDateSearchOptionId(committedHotelDateSelection),
    })
    searchDispatch({
      type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED,
      selected: committedHotelDateSelection.type === 'anytime',
    })
  }, [committedHotelDateSelection, searchDispatch])

  const restoreCommittedRooms = useCallback(() => {
    searchDispatch({
      type: GlobalSearchStateActions.SET_OCCUPANCIES,
      occupancies: cloneOccupancies(committedRoomsRef.current),
    })
  }, [searchDispatch])

  const clearRoomErrors = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ROOM_ERRORS, shown: false })
  }, [searchDispatch])

  const handleRoomChange = useCallback((occupancies: Array<App.Occupants>) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_OCCUPANCIES, occupancies })
  }, [searchDispatch])

  useEffect(() => {
    if (isHotelDatePanelOpen || isHotelGuestsPanelOpen) return
    restoreCommittedDates()
  }, [
    isHotelDatePanelOpen,
    isHotelGuestsPanelOpen,
    restoreCommittedDates,
  ])

  useEffect(() => {
    if (isHotelGuestsPanelOpen) return
    restoreCommittedRooms()
  }, [defaultRooms, isHotelGuestsPanelOpen, restoreCommittedRooms])

  useEffect(() => {
    if (isCompactDatePicker) return

    setIsHotelDatePanelOpen(false)
    setIsHotelGuestsPanelOpen(false)
  }, [isCompactDatePicker])

  const openHotelDatePanel = useCallback(() => {
    restoreCommittedRooms()
    // Don't clear Guests errors while occupancy is still incomplete (e.g. unset ages).
    if (areRoomsValid) {
      clearRoomErrors()
    }
    setIsHotelGuestsPanelOpen(false)
    setIsHotelDatePanelOpen(true)
  }, [areRoomsValid, clearRoomErrors, restoreCommittedRooms])

  const closeHotelDatePanel = useCallback(() => {
    restoreCommittedDates()
    setIsHotelDatePanelOpen(false)
  }, [restoreCommittedDates])

  const confirmHotelDatePanel = useCallback((selectionOverride?: ListingDrawerHotelDateSelection) => {
    commitHotelDateSelection(selectionOverride)
    setIsHotelDatePanelOpen(false)
  }, [commitHotelDateSelection])

  const openHotelGuestsPanel = useCallback(() => {
    restoreCommittedDates()
    setIsHotelDatePanelOpen(false)
    setIsHotelGuestsPanelOpen(true)
  }, [restoreCommittedDates])

  const closeHotelGuestsPanel = useCallback(() => {
    restoreCommittedRooms()
    // Leave areRoomErrorsShown alone — HotelListingSearchControls clears it when valid,
    // and Apply/Search/initial seed are what set it.
    setIsHotelGuestsPanelOpen(false)
  }, [restoreCommittedRooms])

  const confirmHotelGuestsPanel = useCallback(() => {
    if (!areRoomsValid) {
      searchDispatch({ type: GlobalSearchStateActions.TOGGLE_ROOM_ERRORS, shown: true })
      return
    }

    commitRoomsToParent()
    committedRoomsRef.current = cloneOccupancies(draftRooms)
    setIsHotelGuestsPanelOpen(false)
  }, [areRoomsValid, commitRoomsToParent, draftRooms, searchDispatch])

  return {
    isHotelDatePanelOpen,
    isHotelGuestsPanelOpen,
    restoreCommittedDates,
    restoreCommittedRooms,
    clearRoomErrors,
    handleRoomChange,
    openHotelDatePanel,
    closeHotelDatePanel,
    confirmHotelDatePanel,
    openHotelGuestsPanel,
    closeHotelGuestsPanel,
    confirmHotelGuestsPanel,
  }
}

export interface ListingDrawerHotelDateGuestsParams {
  dates: ListingDrawerHotelDateSelection
  rooms: Array<App.Occupants>
  compact: boolean
  maxGuests: number
  onDatesChange: (selection: ListingDrawerHotelDateSelection) => void
  onRoomsChange: (rooms: Array<App.Occupants>) => void
  onCancelDates: () => void
  onSearchDates: (selection: ListingDrawerHotelDateSelection) => void
}

export function useListingDrawerHotelDateGuests({ dates, rooms: committedRooms, compact, maxGuests, onDatesChange, onRoomsChange, onCancelDates, onSearchDates }: ListingDrawerHotelDateGuestsParams) {
  const state = useContext(GlobalSearchStateContext)
  const dispatch = useContext(GlobalSearchDispatchContext)
  const rooms = state.occupancies ?? committedRooms
  const areRoomsValid = rooms.length > 0 && sumUpOccupancies(rooms) <= maxGuests && isValidOccupancy(...rooms)
  const commitDates = useCallback((override?: ListingDrawerHotelDateSelection) => {
    onDatesChange(override ?? getHotelDateSelectionFromSearchState(state))
  }, [onDatesChange, state])
  const commitRooms = useCallback(() => {
    onRoomsChange(rooms)
    dispatch({ type: GlobalSearchStateActions.TOGGLE_ROOM_ERRORS, shown: false })
  }, [dispatch, onRoomsChange, rooms])
  const initiallyIncompleteOccupancy = committedRooms.length > 0 && !isValidOccupancy(...committedRooms)
  const draft = useListingDrawerHotelSearchDraft({
    searchDispatch: dispatch,
    committedHotelDateSelection: dates,
    defaultRooms: committedRooms,
    isCompactDatePicker: compact,
    commitHotelDateSelection: commitDates,
    commitRoomsToParent: commitRooms,
    areRoomsValid,
    draftRooms: rooms,
    initiallyOpenGuestsPanel: initiallyIncompleteOccupancy && compact,
  })
  const { restoreCommittedDates, restoreCommittedRooms, openHotelDatePanel, closeHotelDatePanel, confirmHotelDatePanel, openHotelGuestsPanel, closeHotelGuestsPanel, confirmHotelGuestsPanel, handleRoomChange } = draft
  const closeMenu = useCallback(() => {
    onCancelDates()
    dispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Closed })
  }, [dispatch, onCancelDates])
  const openDates = useCallback(() => {
    if (compact) openHotelDatePanel()
    else dispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Dates })
  }, [compact, dispatch, openHotelDatePanel])
  const openGuests = useCallback(() => {
    if (compact) {
      onCancelDates()
      openHotelGuestsPanel()
    } else dispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Rooms })
  }, [compact, dispatch, onCancelDates, openHotelGuestsPanel])
  const closeDates = useCallback(() => {
    onCancelDates()
    if (compact) closeHotelDatePanel()
    else {
      restoreCommittedDates()
      closeMenu()
    }
  }, [closeHotelDatePanel, closeMenu, compact, onCancelDates, restoreCommittedDates])
  const closeGuests = useCallback(() => {
    if (compact) closeHotelGuestsPanel()
    else {
      restoreCommittedRooms()
      closeMenu()
    }
  }, [closeHotelGuestsPanel, closeMenu, compact, restoreCommittedRooms])
  const confirmDates = useCallback((override?: ListingDrawerHotelDateSelection) => {
    if (compact) confirmHotelDatePanel(override)
    else {
      commitDates(override)
      closeMenu()
    }
  }, [closeMenu, commitDates, compact, confirmHotelDatePanel])
  const confirmGuests = useCallback(() => {
    if (compact) confirmHotelGuestsPanel()
    else {
      if (!areRoomsValid) {
        dispatch({ type: GlobalSearchStateActions.TOGGLE_ROOM_ERRORS, shown: true })
        return
      }
      commitRooms()
      closeMenu()
    }
  }, [areRoomsValid, closeMenu, commitRooms, compact, confirmHotelGuestsPanel, dispatch])
  const unsetDates = useCallback(() => {
    dispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
    dispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
  }, [dispatch])
  const unsetFlexibleDates = useCallback(() => {
    dispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_DURATION })
    dispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_MONTH_RANGE })
    dispatch({ type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS })
  }, [dispatch])
  const selectSpecificDates = useCallback(() => {
    unsetFlexibleDates()
    dispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: false })
    dispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.SPECIFIC })
  }, [dispatch, unsetFlexibleDates])
  const selectFlexibleDates = useCallback(() => {
    unsetDates()
    dispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: false })
    dispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.FLEXIBLE })
    if (state.flexibleNights === DEFAULT_FLEXIBLE_DURATION_RANGE.EMPTY) {
      dispatch({ type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION, flexibleNights: DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION })
    }
  }, [dispatch, state.flexibleNights, unsetDates])
  const selectAnytimeDate = useCallback((shouldApply: boolean) => {
    unsetDates()
    unsetFlexibleDates()
    dispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: true })
    dispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.ANYTIME })
    if (shouldApply) onSearchDates({ type: 'anytime' })
    else commitDates({ type: 'anytime' })
    closeMenu()
  }, [closeMenu, commitDates, dispatch, onSearchDates, unsetDates, unsetFlexibleDates])
  const selectAnytime = useCallback(() => confirmDates({ type: 'anytime' }), [confirmDates])
  const applyDates = useCallback(() => confirmDates(), [confirmDates])
  const dateDropdownProps = useMemo(() => ({
    onDatesApply: applyDates,
    onDatesDecline: closeDates,
    anytimeDateSelected: state.isAnytimeDateSelected,
    selectAnytimeDate,
    selectSpecificDates,
    selectFlexibleDates,
    dateSearchOptionId: state.dateSearchOptionId,
    closeMenu: closeDates,
  }), [applyDates, closeDates, selectAnytimeDate, selectFlexibleDates, selectSpecificDates, state.dateSearchOptionId, state.isAnytimeDateSelected])

  return {
    rooms,
    dateOpen: compact ? draft.isHotelDatePanelOpen : state.activeMenu === SearchMenuStates.Dates,
    guestsOpen: compact ? draft.isHotelGuestsPanelOpen : state.activeMenu === SearchMenuStates.Rooms,
    hasGuestsError: state.areRoomErrorsShown && !areRoomsValid,
    openDates,
    openGuests,
    closeDates,
    closeGuests,
    confirmDates,
    confirmGuests,
    handleRoomChange,
    selectAnytime,
    dateDropdownProps,
  }
}

export default useListingDrawerHotelSearchDraft
